import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import RedirectHandler from "@/components/seo/RedirectHandler";

// Critical path — eagerly loaded
import Index from "./pages/Index";

// Lazy-loaded routes
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const HealthBenefits = lazy(() => import("./pages/HealthBenefits"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Videos = lazy(() => import("./pages/Videos"));
const Certifications = lazy(() => import("./pages/Certifications"));
const NihonTrim = lazy(() => import("./pages/NihonTrim"));
const Research = lazy(() => import("./pages/Research"));
const Dealership = lazy(() => import("./pages/Dealership"));
const Catalogues = lazy(() => import("./pages/Catalogues"));
const Reviews = lazy(() => import("./pages/Reviews"));
const NotFound = lazy(() => import("./pages/NotFound"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const BlogPost = lazy(() => import("./pages/BlogPost"));

// Admin — heavy, always lazy
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminVideos = lazy(() => import("./pages/admin/AdminVideos"));
const AdminDocuments = lazy(() => import("./pages/admin/AdminDocuments"));
const AdminCertifications = lazy(() => import("./pages/admin/AdminCertifications"));
const AdminBranches = lazy(() => import("./pages/admin/AdminBranches"));
const AdminHomepage = lazy(() => import("./pages/admin/AdminHomepage"));
const AdminAbout = lazy(() => import("./pages/admin/AdminAbout"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminRedirects = lazy(() => import("./pages/admin/AdminRedirects"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const AdminResearch = lazy(() => import("./pages/admin/AdminResearch"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminClientGallery = lazy(() => import("./pages/admin/AdminClientGallery"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <RedirectHandler />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/:id" element={<ProductDetail />} />
                <Route path="/health-benefits" element={<HealthBenefits />} />
                <Route path="/about-us" element={<About />} />
                <Route path="/contact-us" element={<Contact />} />
                <Route path="/videos" element={<Videos />} />
                <Route path="/certifications" element={<Certifications />} />
                <Route path="/nihon-trim" element={<NihonTrim />} />
                <Route path="/research" element={<Research />} />
                <Route path="/dealership-request-form" element={<Dealership />} />
                <Route path="/catalogues" element={<Catalogues />} />
                <Route path="/reviews" element={<Reviews />} />
                <Route path="/blog" element={<BlogIndex />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/admin-login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="videos" element={<AdminVideos />} />
                  <Route path="documents" element={<AdminDocuments />} />
                  <Route path="certifications" element={<AdminCertifications />} />
                  <Route path="branches" element={<AdminBranches />} />
                  <Route path="homepage" element={<AdminHomepage />} />
                  <Route path="about" element={<AdminAbout />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="redirects" element={<AdminRedirects />} />
                  <Route path="leads" element={<AdminLeads />} />
                  <Route path="research" element={<AdminResearch />} />
                  <Route path="reviews" element={<AdminReviews />} />
                  <Route path="blog" element={<AdminBlog />} />
                  <Route path="gallery" element={<AdminClientGallery />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
